import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import List from 'app/components/List';
import {ApiFeature, ApiFeatureCommonProperties} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {makeUserListItems} from 'app/pages/PropertyOverview/utils';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {recordEvent} from 'app/tools/Analytics';
import * as CONSTANTS from 'app/utils/constants';
import * as featureUtils from 'app/utils/featureUtils';

interface Props {
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  /** The properties the change applies to. */
  features: I.ImmutableOf<ApiFeature[]>;
  organizationUsers: ApiOrganizationUser[];
  batchPatchFeature: FeaturesActions['batchPatchFeature'];
  menuClassName?: string;
  /** The popover's trigger. */
  children: React.ReactElement;
}

/**
 * Menu that assigns or unassigns one person across every given property, or
 * clears their assignees.
 */
export const AssigneeMenu: React.FC<Props> = ({
  featureCollection,
  features,
  organizationUsers,
  batchPatchFeature,
  menuClassName,
  children,
}) => {
  // Drives both the checkmarks and what clicking one does: a name that isn't on
  // every given property gets added to all of them, otherwise it gets removed.
  const isAssignedToAll = (email: string) =>
    features.size > 0 &&
    features.every((f) => featureUtils.getFeatureAssignees(f!).includes(email));

  const onClickItem = ({id}: {id: string}) => {
    // Only a batch is worth confirming: clearing one property is a change the
    // user can see and redo in place.
    if (
      id === '' &&
      features.size > 1 &&
      !confirm('Are you sure you want to clear assignees for selected properties?')
    ) {
      return;
    }

    const isAdding = !!id && !isAssignedToAll(id);

    // Assignees are stored as a map so a single request can flip one person across
    // every selected property, leaving anyone else assigned to them untouched.
    // Clearing is the only action that has to name the whole set.
    const assigneeEmails: Record<string, boolean> = id
      ? {[id]: isAdding}
      : Object.fromEntries(
          features
            .flatMap((f) => featureUtils.getFeatureAssignees(f!))
            .toArray()
            .map((email) => [email as string, false])
        );

    const properties: Partial<ApiFeatureCommonProperties> = {
      [CONSTANTS.APP_PROPERTIES_KEY]: {
        [CONSTANTS.APP_PROPERTY_ASSIGNEE_EMAILS]: assigneeEmails,
      },
    };

    batchPatchFeature({
      featureCollectionId: featureCollection.get('id'),
      features,
      changes: I.fromJS({properties}),
    });

    recordEvent(isAdding ? 'Set assignee' : 'Unset assignee', {
      featureCollectionId: featureCollection.get('id'),
      featureCount: features.size,
    });
  };

  return (
    <B.Popover
      position={B.Position.BOTTOM_RIGHT}
      modifiers={{arrow: {enabled: false}}}
      content={
        <List
          className={menuClassName}
          items={[
            {id: '', text: 'Remove Assignment'},
            {id: 'sep', text: '', isSeparator: true},
            ...makeUserListItems(organizationUsers, (u) => isAssignedToAll(u.email), true),
          ]}
          onClickItem={onClickItem}
        />
      }
    >
      {children}
    </B.Popover>
  );
};
